package co.maytes.standupbot.dashboard.features.standupruns.tabs

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.features.standupruns.formatCents
import co.maytes.standupbot.dashboard.features.standupruns.surfaceSubtleRgb
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.standuprun.StandupRunDetailDto
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText

@Composable
internal fun AiCostTab(detail: StandupRunDetailDto) {
    val totalInput = detail.aiInputTokens ?: 0
    val totalOutput = detail.aiOutputTokens ?: 0
    val duration = detail.generationDurationMs ?: 0L

    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(topBottom = Spacing.sm)
                .styleModifier {
                    property("border-bottom", "1px solid ${surfaceSubtleRgb()}")
                },
        ) {
            HeaderCell("Model", flex = "2 1 0")
            HeaderCell("Input tokens", flex = "1 1 0", alignRight = true)
            HeaderCell("Output tokens", flex = "1 1 0", alignRight = true)
            HeaderCell("Latency", flex = "1 1 0", alignRight = true)
            HeaderCell("Cost", flex = "1 1 0", alignRight = true)
        }
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(topBottom = Spacing.sm)
                .styleModifier {
                    property("border-bottom", "1px solid ${surfaceSubtleRgb()}")
                },
        ) {
            BodyCell(text = detail.promptVersion ?: "claude", flex = "2 1 0")
            BodyCell(text = totalInput.toString(), flex = "1 1 0", alignRight = true, mono = true)
            BodyCell(text = totalOutput.toString(), flex = "1 1 0", alignRight = true, mono = true)
            BodyCell(text = "${duration}ms", flex = "1 1 0", alignRight = true, mono = true)
            BodyCell(text = formatCents(detail.aiCostCents), flex = "1 1 0", alignRight = true, mono = true)
        }
        AiDegradeNotices.keySourceLabel(detail.aiKeySource)?.let { label ->
            Row(modifier = Modifier.fillMaxWidth().margin(top = Spacing.md)) {
                SpanText(
                    text = "Key source",
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .fontWeight(600)
                        .color(Palette.textSecondary)
                        .margin(right = Spacing.sm),
                )
                SpanText(
                    text = label,
                    modifier = Modifier
                        .fontSize(Typography.bodySmall.size)
                        .color(Palette.textPrimary),
                )
            }
        }
        if (detail.aiDegraded) {
            val isFullFallback = detail.promptVersion == "fallback"
            val notice = AiDegradeNotices.forCode(detail.aiDegradedReason)
            val title = when {
                notice != null && isFullFallback -> "AI was off for this run"
                notice != null -> "AI was off for some sections"
                isFullFallback -> "AI synthesis unavailable — non-AI fallback used for the entire standup."
                else -> "Some sections couldn't synthesize — others ran normally."
            }
            SpanText(
                text = title,
                modifier = Modifier
                    .margin(top = Spacing.md)
                    .fontSize(Typography.bodySmall.size)
                    .fontWeight(600)
                    .color(if (isFullFallback) Palette.error else Palette.warning),
            )
            notice?.let { AiDegradeReasonRow(it) }
            detail.errorMessage?.takeIf { it.isNotBlank() }?.let { msg ->
                SpanText(
                    text = msg,
                    modifier = Modifier
                        .margin(top = Spacing.xxs)
                        .fontSize(Typography.caption.size)
                        .color(Palette.textSecondary)
                        .styleModifier { property("font-family", Typography.FONT_MONO) },
                )
            }
        }

        SystemPromptBlock(detail.promptVersion)
    }
}

@Composable
private fun AiDegradeReasonRow(notice: AiDegradeNotice) {
    Row(
        modifier = Modifier.fillMaxWidth().margin(top = Spacing.xs),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            text = "Reason",
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(600)
                .color(Palette.textSecondary)
                .margin(right = Spacing.sm),
        )
        SpanText(
            text = notice.reason,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textPrimary)
                .margin(right = Spacing.md),
        )
        notice.cta?.let { cta ->
            Link(
                path = cta.href,
                text = "${cta.label} →",
                modifier = Modifier
                    .fontSize(Typography.bodySmall.size)
                    .fontWeight(500)
                    .color(Palette.accent),
                openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
            )
        }
    }
}

@Composable
private fun HeaderCell(label: String, flex: String, alignRight: Boolean = false) {
    Box(
        modifier = Modifier.styleModifier {
            property("flex", flex)
            property("text-align", if (alignRight) "right" else "left")
        },
    ) {
        SpanText(
            text = label,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(600)
                .color(Palette.textSecondary),
        )
    }
}

@Composable
private fun BodyCell(text: String, flex: String, alignRight: Boolean = false, mono: Boolean = false) {
    Box(
        modifier = Modifier.styleModifier {
            property("flex", flex)
            property("text-align", if (alignRight) "right" else "left")
            if (mono) property("font-family", Typography.FONT_MONO)
        },
    ) {
        SpanText(
            text = text,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textPrimary),
        )
    }
}
